import { useState } from "react";
import VoiceTextarea from "../components/VoiceTextarea";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Plus, Trash2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";

const SUPPLIER_TYPES = [
  { v: "manufacturer", l: "Manufacturer" },
  { v: "distributor", l: "Distributor" },
  { v: "agent", l: "Agent" },
  { v: "oem", l: "OEM" },
  { v: "service", l: "Service partner" },
];
const STATUSES = [
  { v: "active", l: "Active" },
  { v: "preferred", l: "Preferred" },
  { v: "inactive", l: "Inactive" },
  { v: "blocked", l: "Blocked" },
];
const PAYMENT_TERMS = [
  { v: "COD", l: "COD" },
  { v: "7d", l: "7 days" },
  { v: "14d", l: "14 days" },
  { v: "30d", l: "30 days end-of-month" },
  { v: "60d", l: "60 days" },
  { v: "custom", l: "Custom" },
];
const TAG_SUGGESTIONS = ["pumps","ro","membranes","filtration","dosing","sensors","valves","chemistry","tanks","controls","spares","piping"];

export default function SupplierNew() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: "",
    supplier_type: "distributor",
    status: "active",
    website: "",
    registration_number: "",
    vat_number: "",
    primary_email: "",
    primary_phone: "",
    physical_address: "",
    billing_address: "",
    country: "South Africa",
    payment_terms: "30d",
    payment_terms_custom: "",
    currency: "ZAR",
    lead_time_days: "",
    our_account_number: "",
    credit_limit: "",
    tags: [],
    notes: "",
    contacts: [],
  });

  const update = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const toggleTag = (t) => {
    setF((p) => ({ ...p, tags: p.tags.includes(t) ? p.tags.filter((x) => x !== t) : [...p.tags, t] }));
  };

  const addContact = () => setF((p) => ({ ...p, contacts: [...p.contacts, { name: "", role: "", email: "", phone: "" }] }));
  const updContact = (i, k, v) => setF((p) => ({ ...p, contacts: p.contacts.map((c, j) => j === i ? { ...c, [k]: v } : c) }));
  const rmContact = (i) => setF((p) => ({ ...p, contacts: p.contacts.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required."); return; }
    setBusy(true);
    try {
      const payload = { ...f };
      if (payload.lead_time_days === "") delete payload.lead_time_days; else payload.lead_time_days = Number(payload.lead_time_days);
      if (payload.credit_limit === "") delete payload.credit_limit; else payload.credit_limit = Number(payload.credit_limit);
      // strip empty contacts
      payload.contacts = payload.contacts.filter((c) => c.name);
      const { data } = await api.post("/suppliers", payload);
      toast.success(`Created — ${data.external_ref}`);
      nav(`/suppliers/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create supplier.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="supplier-new">
      <div className="flex items-center gap-2">
        <Link to="/suppliers" className="hv-btn hv-btn-ghost text-xs"><ArrowLeft className="w-4 h-4" /></Link>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">New supplier</h1>
      </div>

      <div className="hv-card p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1]">Identity</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] text-[#a0aabf]">Name *</span>
            <input type="text" value={f.name} onChange={(e) => update("name", e.target.value)} className="hv-input text-sm w-full" data-testid="supplier-input-name" />
          </label>
          <label className="block">
            <span className="text-[11px] text-[#a0aabf]">Type</span>
            <select value={f.supplier_type} onChange={(e) => update("supplier_type", e.target.value)} className="hv-input text-sm w-full" data-testid="supplier-input-type">
              {SUPPLIER_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] text-[#a0aabf]">Status</span>
            <select value={f.status} onChange={(e) => update("status", e.target.value)} className="hv-input text-sm w-full">
              {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] text-[#a0aabf]">Website</span>
            <input type="url" value={f.website} onChange={(e) => update("website", e.target.value)} className="hv-input text-sm w-full" placeholder="https://" />
          </label>
          <label className="block">
            <span className="text-[11px] text-[#a0aabf]">Registration #</span>
            <input type="text" value={f.registration_number} onChange={(e) => update("registration_number", e.target.value)} className="hv-input text-sm w-full" />
          </label>
          <label className="block">
            <span className="text-[11px] text-[#a0aabf]">VAT #</span>
            <input type="text" value={f.vat_number} onChange={(e) => update("vat_number", e.target.value)} className="hv-input text-sm w-full" />
          </label>
        </div>
      </div>

      <div className="hv-card p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1]">Contact</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] text-[#a0aabf]">Primary email</span>
            <input type="email" value={f.primary_email} onChange={(e) => update("primary_email", e.target.value)} className="hv-input text-sm w-full" />
          </label>
          <label className="block">
            <span className="text-[11px] text-[#a0aabf]">Primary phone</span>
            <input type="tel" value={f.primary_phone} onChange={(e) => update("primary_phone", e.target.value)} className="hv-input text-sm w-full" />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[11px] text-[#a0aabf]">Physical address</span>
            <input type="text" value={f.physical_address} onChange={(e) => update("physical_address", e.target.value)} className="hv-input text-sm w-full" />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[11px] text-[#a0aabf]">Billing address</span>
            <input type="text" value={f.billing_address} onChange={(e) => update("billing_address", e.target.value)} className="hv-input text-sm w-full" />
          </label>
          <label className="block">
            <span className="text-[11px] text-[#a0aabf]">Country</span>
            <input type="text" value={f.country} onChange={(e) => update("country", e.target.value)} className="hv-input text-sm w-full" />
          </label>
        </div>
      </div>

      <div className="hv-card p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1]">Contacts</div>
          <button onClick={addContact} className="hv-btn hv-btn-ghost text-xs" data-testid="supplier-add-contact"><Plus className="w-3 h-3" /> Add contact</button>
        </div>
        {f.contacts.length === 0 ? (
          <div className="text-xs text-[#a0aabf]">No contacts yet.</div>
        ) : (
          <div className="space-y-2">
            {f.contacts.map((c, i) => (
              <div key={i} className="grid sm:grid-cols-4 gap-2 items-center">
                <input placeholder="Name" value={c.name} onChange={(e) => updContact(i, "name", e.target.value)} className="hv-input text-sm" />
                <input placeholder="Role" value={c.role} onChange={(e) => updContact(i, "role", e.target.value)} className="hv-input text-sm" />
                <input placeholder="Email" value={c.email} onChange={(e) => updContact(i, "email", e.target.value)} className="hv-input text-sm" />
                <div className="flex gap-1">
                  <input placeholder="Phone" value={c.phone} onChange={(e) => updContact(i, "phone", e.target.value)} className="hv-input text-sm flex-1" />
                  <button onClick={() => rmContact(i)} className="hv-btn hv-btn-ghost text-xs" title="Remove"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="hv-card p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1]">Trading terms</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-[11px] text-[#a0aabf]">Payment terms</span>
            <select value={f.payment_terms} onChange={(e) => update("payment_terms", e.target.value)} className="hv-input text-sm w-full">
              {PAYMENT_TERMS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
          </label>
          {f.payment_terms === "custom" && (
            <label className="block">
              <span className="text-[11px] text-[#a0aabf]">Custom terms</span>
              <input value={f.payment_terms_custom} onChange={(e) => update("payment_terms_custom", e.target.value)} className="hv-input text-sm w-full" />
            </label>
          )}
          <label className="block">
            <span className="text-[11px] text-[#a0aabf]">Currency</span>
            <input value={f.currency} onChange={(e) => update("currency", e.target.value)} className="hv-input text-sm w-full" />
          </label>
          <label className="block">
            <span className="text-[11px] text-[#a0aabf]">Lead time (days)</span>
            <input type="number" value={f.lead_time_days} onChange={(e) => update("lead_time_days", e.target.value)} className="hv-input text-sm w-full" />
          </label>
          <label className="block">
            <span className="text-[11px] text-[#a0aabf]">Our account # at supplier</span>
            <input value={f.our_account_number} onChange={(e) => update("our_account_number", e.target.value)} className="hv-input text-sm w-full" />
          </label>
          <label className="block">
            <span className="text-[11px] text-[#a0aabf]">Credit limit</span>
            <input type="number" value={f.credit_limit} onChange={(e) => update("credit_limit", e.target.value)} className="hv-input text-sm w-full" />
          </label>
        </div>
      </div>

      <div className="hv-card p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1]">Categories</div>
        <div className="flex flex-wrap gap-1.5">
          {TAG_SUGGESTIONS.map((t) => {
            const on = f.tags.includes(t);
            return (
              <button key={t} type="button" onClick={() => toggleTag(t)} className={`text-[11px] px-2 py-1 rounded border transition ${on ? "border-[#1FB6C1] bg-[#1FB6C1]/[0.07] text-[#1FB6C1]" : "border-white/10 hover:border-[#1FB6C1]/40 text-[#cbd2df]"}`}>
                {t}
              </button>
            );
          })}
        </div>
        <label className="block">
          <span className="text-[11px] text-[#a0aabf]">Notes</span>
          <VoiceTextarea value={f.notes} onChange={(v) => update("notes", v)} rows={4} context="supplier_notes" testIdPrefix="supplier-notes-voice" data-testid="supplier-notes" />
        </label>
      </div>

      <div className="flex justify-end gap-2 pb-6">
        <Link to="/suppliers" className="hv-btn hv-btn-ghost text-sm">Cancel</Link>
        <button onClick={save} disabled={busy} className="hv-btn hv-btn-primary text-sm" data-testid="supplier-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Create
        </button>
      </div>
    </div>
  );
}
